import { useEffect, useMemo, useState, useContext } from 'react';
import { loadStripe } from '@stripe/stripe-js';
import {
  Elements,
  PaymentElement,
  useElements,
  useStripe,
} from '@stripe/react-stripe-js';
import { useTranslation } from 'react-i18next';
import { Button } from '../../components/ui/button';
import { CreditCard, Loader2, AlertCircle } from 'lucide-react';
import { toast } from 'sonner';
import { AuthContext } from '../../contexts/auth';
import { register as registerApi } from '../../api/auth';
import {
  initPro,
  finalizePro,
  abortPending,
  createSubscriptionIntent,
  cancelIncompleteSubscription,
} from '../../api/subscription';

const PUBLISHABLE_KEY = process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY;
const stripePromise = PUBLISHABLE_KEY ? loadStripe(PUBLISHABLE_KEY) : null;

function PaymentForm({ onPaid, onTooManyFailures }) {
  const stripe = useStripe();
  const elements = useElements();
  const { t } = useTranslation();
  const [submitting, setSubmitting] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [attempts, setAttempts] = useState(0);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!stripe || !elements || submitting) return;

    setSubmitting(true);
    setErrorMessage('');
    try {
      const { error, paymentIntent } = await stripe.confirmPayment({
        elements,
        redirect: 'if_required',
      });

      if (error) {
        const nextAttempts = attempts + 1;
        setAttempts(nextAttempts);
        setErrorMessage(error.message || t('paymentDeclined'));
        if (nextAttempts >= 2) onTooManyFailures();
        return;
      }

      if (paymentIntent && paymentIntent.status === 'succeeded') {
        await onPaid();
      } else {
        toast.info(t('paymentProcessing'));
      }
    } catch (err) {
      console.error(err);
      setErrorMessage(err?.message || t('paymentUnexpectedError'));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-5">
      <PaymentElement options={{ layout: 'tabs' }} />

      {errorMessage && (
        <div className="flex items-start gap-2 rounded-md border border-red-200 bg-red-50 p-3 text-sm text-red-700">
          <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" />
          <span>{errorMessage}</span>
        </div>
      )}

      <Button
        type="submit"
        disabled={!stripe || submitting}
        className="w-full h-12 bg-gradient-to-r from-blue-600 to-indigo-700 hover:from-blue-700 hover:to-indigo-800"
      >
        {submitting ? (
          <Loader2 className="w-4 h-4 animate-spin" />
        ) : (
          <>
            <CreditCard className="w-4 h-4 mr-2" />
            {t('payAndFinish')}
          </>
        )}
      </Button>
    </form>
  );
}

function FallbackOffer({ onContinueFree, onCancel, loading }) {
  const { t } = useTranslation();
  return (
    <div className="space-y-4 rounded-lg border border-amber-200 bg-amber-50 p-4">
      <div className="flex items-start gap-2">
        <AlertCircle className="mt-0.5 h-5 w-5 shrink-0 text-amber-600" />
        <div className="space-y-1">
          <p className="font-semibold text-amber-900">{t('paymentRetryFailedTitle')}</p>
          <p className="text-sm text-amber-800">{t('paymentRetryFailedDescription')}</p>
        </div>
      </div>
      <div className="flex gap-2">
        <Button variant="outline" onClick={onCancel} disabled={loading} className="flex-1">
          {t('keepTrying')}
        </Button>
        <Button onClick={onContinueFree} disabled={loading} className="flex-1">
          {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : t('continueAsFree')}
        </Button>
      </div>
    </div>
  );
}

export function PaymentStep({ pendingRegistration, onSuccess, onFallbackToFree }) {
  const { t } = useTranslation();
  const { setUser, storageUser, user } = useContext(AuthContext);
  const [pending, setPending] = useState(null); // { subscriptionId, customerId, clientSecret }
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showFallback, setShowFallback] = useState(false);
  const [fallbackLoading, setFallbackLoading] = useState(false);

  const isUpgrade = !pendingRegistration; // upgrade de user ja autenticado

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        setLoading(true);
        setError('');
        let data;
        if (isUpgrade) {
          data = await createSubscriptionIntent();
        } else {
          data = await initPro(pendingRegistration);
        }
        if (!cancelled) {
          setPending({
            subscriptionId: data.subscriptionId,
            customerId: data.customerId || null,
            clientSecret: data.clientSecret,
          });
        }
      } catch (err) {
        console.error(err);
        if (!cancelled) setError(err?.response?.data?.error || t('paymentIntentError'));
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [isUpgrade, pendingRegistration, t]);

  const handlePaid = async () => {
    if (isUpgrade) {
      // Upgrade: user ja existe; recarrega profile pra refletir plano novo
      onSuccess();
      return;
    }

    // Cadastro novo: chama finalize-pro pra criar User + receber {user, token}
    try {
      const { user: createdUser, token } = await finalizePro({
        subscriptionId: pending.subscriptionId,
        customerId: pending.customerId,
        ...pendingRegistration,
      });
      setUser(createdUser);
      storageUser(createdUser, token, true);
      toast.success(t('welcomePro'));
      onSuccess();
    } catch (err) {
      console.error(err);
      toast.error(err?.response?.data?.error || t('paymentUnexpectedError'));
    }
  };

  const handleContinueFree = async () => {
    setFallbackLoading(true);
    try {
      if (isUpgrade) {
        // user logado: cancela subscription incomplete e mantem como Free
        await cancelIncompleteSubscription();
        const updatedUser = { ...user, plano: 'free', planId: null, planStatus: null };
        setUser(updatedUser);
        const token = localStorage.getItem('token') || sessionStorage.getItem('token');
        const remember = !!localStorage.getItem('token');
        storageUser(updatedUser, token, remember);
      } else {
        // cadastro pendente: cancela no Stripe + cria User como Free via /auth/register
        await abortPending({
          subscriptionId: pending?.subscriptionId,
          customerId: pending?.customerId,
        });
        const { user: createdUser, token } = await registerApi(
          pendingRegistration.nome,
          pendingRegistration.email,
          pendingRegistration.password
        );
        setUser(createdUser);
        storageUser(createdUser, token, true);
      }
      toast.success(t('continuingAsFree'));
      onFallbackToFree();
    } catch (err) {
      console.error(err);
      toast.error(err?.response?.data?.error || t('paymentUnexpectedError'));
    } finally {
      setFallbackLoading(false);
    }
  };

  const options = useMemo(
    () => (pending?.clientSecret ? { clientSecret: pending.clientSecret, appearance: { theme: 'stripe' } } : null),
    [pending?.clientSecret]
  );

  if (!stripePromise) {
    return (
      <div className="p-8 text-center text-red-600">
        REACT_APP_STRIPE_PUBLISHABLE_KEY não configurada.
      </div>
    );
  }

  if (loading) {
    return (
      <div className="p-8 flex flex-col items-center gap-3 text-slate-600">
        <Loader2 className="w-6 h-6 animate-spin" />
        <p>{t('preparingPayment')}</p>
      </div>
    );
  }

  if (error || !pending?.clientSecret) {
    return (
      <div className="p-8 space-y-4">
        <div className="flex items-start gap-2 rounded-md border border-red-200 bg-red-50 p-3 text-sm text-red-700">
          <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" />
          <span>{error || t('paymentIntentError')}</span>
        </div>
        <Button onClick={handleContinueFree} disabled={fallbackLoading} className="w-full">
          {fallbackLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : t('continueAsFree')}
        </Button>
      </div>
    );
  }

  return (
    <div className="p-8 space-y-6">
      <div className="text-center space-y-1">
        <p className="text-sm uppercase tracking-wider text-blue-600 font-semibold">
          {t('paymentStepLabel')}
        </p>
        <h2 className="text-xl font-bold text-slate-900">{t('paymentStepTitle')}</h2>
        <p className="text-sm text-slate-600">{t('paymentStepDescription')}</p>
      </div>

      {showFallback ? (
        <FallbackOffer
          loading={fallbackLoading}
          onContinueFree={handleContinueFree}
          onCancel={() => setShowFallback(false)}
        />
      ) : (
        <Elements stripe={stripePromise} options={options}>
          <PaymentForm
            onPaid={handlePaid}
            onTooManyFailures={() => setShowFallback(true)}
          />
        </Elements>
      )}
    </div>
  );
}

export default PaymentStep;
